// LuviaRx — Admin modal to record a walk-in / phone / cash / Venmo / comp order.
// No customer account is created; guest info is stored inline on the order.
// The order is inserted into the same `orders` collection as online orders,
// which means it also decrements inventory (via the reserved rollup) and
// shows up in every admin list / revenue view.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X as XIcon, Plus, Trash2, Loader2 } from "lucide-react";
import { US_STATES } from "../../data/usStates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHOD_OPTIONS = [
  { value: "cash",             label: "Cash" },
  { value: "venmo",            label: "Venmo" },
  { value: "zelle",            label: "Zelle (off-platform)" },
  { value: "stripe_external",  label: "Card (external terminal)" },
  { value: "comp",             label: "Comp (no charge)" },
  { value: "other",            label: "Other" },
];

const STATUS_OPTIONS = [
  "Pending Payment - Zelle",
  "Payment Confirmed",
  "Shipped / Fulfilled",
  "Cancelled",
];

const usd = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ManualOrderModal({ token, onClose, onCreated }) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [shipping, setShipping] = useState({
    include: false,
    first_name: "", last_name: "", address1: "", address2: "",
    city: "", state: "", zip_code: "", phone: "",
  });
  const [items, setItems] = useState([
    { product_id: "", variant_id: "", quantity: 1, unit_price_override: "" },
  ]);
  const [shippingTotal, setShippingTotal] = useState("0.00");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNote, setPaymentNote] = useState("");
  const [status, setStatus] = useState("Payment Confirmed");
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load the product catalog once for the variant picker.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/products`)
      .then(({ data }) => { if (!cancelled) setProducts(data || []); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setProductsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const productMap = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p.slug, p));
    return m;
  }, [products]);

  const lineSubtotal = useMemo(() => {
    return items.reduce((acc, it) => {
      const p = productMap.get(it.product_id);
      const v = p?.variants?.find((x) => x.id === it.variant_id);
      if (!p || !v) return acc;
      const unit = it.unit_price_override !== "" && it.unit_price_override !== null
        ? Number(it.unit_price_override) || 0
        : Number(v.price) || 0;
      return acc + unit * (Number(it.quantity) || 0);
    }, 0);
  }, [items, productMap]);
  const totalPreview = lineSubtotal + (Number(shippingTotal) || 0);

  const setItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () =>
    setItems((prev) => [...prev, { product_id: "", variant_id: "", quantity: 1, unit_price_override: "" }]);
  const removeItem = (idx) =>
    setItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setError("");
    if (!customer.name.trim()) {
      setError("Customer name is required.");
      return;
    }
    const cleanedItems = items
      .filter((it) => it.product_id && it.variant_id && Number(it.quantity) > 0)
      .map((it) => ({
        product_id: it.product_id,
        variant_id: it.variant_id,
        quantity: Number(it.quantity),
        unit_price_override:
          it.unit_price_override !== "" && it.unit_price_override !== null
            ? Number(it.unit_price_override)
            : null,
      }));
    if (cleanedItems.length === 0) {
      setError("Add at least one line item with a product + quantity.");
      return;
    }
    if (sendConfirmation && !customer.email.trim()) {
      setError("Email is required to send a customer confirmation.");
      return;
    }
    const payload = {
      customer_name: customer.name.trim(),
      customer_email: customer.email.trim() || null,
      customer_phone: customer.phone.trim() || null,
      items: cleanedItems,
      payment_method: paymentMethod,
      payment_note: paymentNote.trim() || null,
      status,
      shipping_total: Number(shippingTotal) || 0,
      send_confirmation_email: sendConfirmation && !!customer.email.trim(),
    };
    if (shipping.include) {
      payload.shipping = {
        first_name: shipping.first_name.trim() || customer.name.split(" ")[0] || "Guest",
        last_name: shipping.last_name.trim() || customer.name.split(" ").slice(1).join(" ") || "-",
        address1: shipping.address1.trim(),
        address2: shipping.address2.trim() || undefined,
        city: shipping.city.trim(),
        state: shipping.state,
        zip_code: shipping.zip_code.trim(),
        phone: shipping.phone.trim() || customer.phone.trim() || "n/a",
      };
      // Server ShippingAddress requires all core fields — validate here to
      // fail fast rather than showing a Pydantic 422.
      const missing = ["address1", "city", "state", "zip_code"].filter(
        (k) => !payload.shipping[k],
      );
      if (missing.length) {
        setError(`Shipping address is missing: ${missing.join(", ")}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/manual`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to create order.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="manual-order-modal"
      className="fixed inset-0 z-[60] flex items-start justify-center bg-charcoal-800/60 backdrop-blur-sm overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-cream-100 rounded-3xl shadow-2xl border border-charcoal-100 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-charcoal-100">
          <div>
            <p className="overline text-[#8B2252]">New Manual Order</p>
            <h2 className="font-display text-2xl text-charcoal-800 mt-1">
              Record a walk-in or off-platform order
            </h2>
            <p className="text-sm text-charcoal-500 mt-1 max-w-xl">
              For phone, cash, Venmo, or comp orders where no account or Stripe checkout ran.
              Stock is reserved automatically once this order is saved.
            </p>
          </div>
          <button
            type="button"
            data-testid="manual-order-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-charcoal-100 text-charcoal-500"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Customer */}
          <section>
            <p className="overline mb-3">Customer</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                data-testid="manual-order-customer-name"
                className="input"
                placeholder="Full name *"
                value={customer.name}
                onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
              />
              <input
                data-testid="manual-order-customer-email"
                className="input"
                type="email"
                placeholder="Email (optional)"
                value={customer.email}
                onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))}
              />
              <input
                data-testid="manual-order-customer-phone"
                className="input"
                type="tel"
                placeholder="Phone (optional)"
                value={customer.phone}
                onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
              />
            </div>
          </section>

          {/* Shipping */}
          <section>
            <label className="inline-flex items-center gap-2 text-sm text-charcoal-700 cursor-pointer mb-3">
              <input
                data-testid="manual-order-include-shipping"
                type="checkbox"
                checked={shipping.include}
                onChange={(e) => setShipping((s) => ({ ...s, include: e.target.checked }))}
              />
              Add a shipping address (skip for walk-ins / in-person pickup)
            </label>
            {shipping.include && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="input" placeholder="First name"
                  value={shipping.first_name}
                  onChange={(e) => setShipping((s) => ({ ...s, first_name: e.target.value }))} />
                <input className="input" placeholder="Last name"
                  value={shipping.last_name}
                  onChange={(e) => setShipping((s) => ({ ...s, last_name: e.target.value }))} />
                <input className="input md:col-span-2" placeholder="Address line 1 *"
                  value={shipping.address1}
                  onChange={(e) => setShipping((s) => ({ ...s, address1: e.target.value }))} />
                <input className="input md:col-span-2" placeholder="Address line 2"
                  value={shipping.address2}
                  onChange={(e) => setShipping((s) => ({ ...s, address2: e.target.value }))} />
                <input className="input" placeholder="City *"
                  value={shipping.city}
                  onChange={(e) => setShipping((s) => ({ ...s, city: e.target.value }))} />
                <select
                  className="input"
                  value={shipping.state}
                  onChange={(e) => setShipping((s) => ({ ...s, state: e.target.value }))}
                >
                  <option value="">State *</option>
                  {US_STATES.map((st) => (
                    <option key={st.code} value={st.code}>{st.name}</option>
                  ))}
                </select>
                <input className="input" placeholder="ZIP *"
                  value={shipping.zip_code}
                  onChange={(e) => setShipping((s) => ({ ...s, zip_code: e.target.value }))} />
                <input className="input" placeholder="Phone"
                  value={shipping.phone}
                  onChange={(e) => setShipping((s) => ({ ...s, phone: e.target.value }))} />
              </div>
            )}
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Line items</p>
              <button
                type="button"
                data-testid="manual-order-add-item"
                onClick={addItem}
                className="text-xs px-3 py-1.5 rounded-full border border-charcoal-200 hover:bg-cream-200 inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add item
              </button>
            </div>
            {productsLoading ? (
              <p className="text-sm text-charcoal-400">Loading catalog…</p>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => {
                  const p = productMap.get(it.product_id);
                  const variants = p?.variants || [];
                  const v = variants.find((x) => x.id === it.variant_id);
                  return (
                    <div
                      key={idx}
                      data-testid={`manual-order-line-${idx}`}
                      className="grid grid-cols-12 gap-2 items-center bg-white p-3 rounded-2xl border border-charcoal-100"
                    >
                      <select
                        className="input col-span-4"
                        data-testid={`manual-order-line-${idx}-product`}
                        value={it.product_id}
                        onChange={(e) => setItem(idx, { product_id: e.target.value, variant_id: "" })}
                      >
                        <option value="">Select product</option>
                        {products.map((pp) => (
                          <option key={pp.slug} value={pp.slug}>{pp.name}</option>
                        ))}
                      </select>
                      <select
                        className="input col-span-3"
                        data-testid={`manual-order-line-${idx}-variant`}
                        value={it.variant_id}
                        onChange={(e) => setItem(idx, {
                          variant_id: e.target.value,
                          unit_price_override:
                            it.unit_price_override === "" ? "" : it.unit_price_override,
                        })}
                        disabled={!it.product_id}
                      >
                        <option value="">Variant</option>
                        {variants.map((vv) => (
                          <option key={vv.id} value={vv.id}>
                            {vv.label} — ${vv.price}
                            {vv.stock != null ? ` (${vv.stock} in stock)` : ""}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input col-span-2"
                        type="number" min={1} max={100}
                        data-testid={`manual-order-line-${idx}-qty`}
                        value={it.quantity}
                        onChange={(e) => setItem(idx, { quantity: e.target.value })}
                        placeholder="Qty"
                      />
                      <input
                        className="input col-span-2"
                        type="number" min={0} step="0.01"
                        data-testid={`manual-order-line-${idx}-price`}
                        value={it.unit_price_override}
                        onChange={(e) => setItem(idx, { unit_price_override: e.target.value })}
                        placeholder={v ? `$${v.price}` : "Unit $"}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                        data-testid={`manual-order-line-${idx}-remove`}
                        className="col-span-1 justify-self-end p-2 rounded-full hover:bg-red-50 text-red-600 disabled:opacity-30"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Totals */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="overline mb-2">Shipping charge</p>
              <input
                className="input"
                type="number" min={0} step="0.01"
                data-testid="manual-order-shipping-total"
                value={shippingTotal}
                onChange={(e) => setShippingTotal(e.target.value)}
              />
            </div>
            <div className="bg-white rounded-2xl border border-charcoal-100 p-4">
              <div className="flex justify-between text-sm text-charcoal-500">
                <span>Subtotal</span><span data-testid="manual-order-subtotal">{usd(lineSubtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-charcoal-500 mt-1">
                <span>Shipping</span><span>{usd(shippingTotal)}</span>
              </div>
              <div className="flex justify-between text-base font-medium text-charcoal-800 mt-2 border-t border-charcoal-100 pt-2">
                <span>Total</span><span data-testid="manual-order-total">{usd(totalPreview)}</span>
              </div>
            </div>
          </section>

          {/* Payment + status */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="overline mb-2">Payment method</p>
              <select
                className="input"
                data-testid="manual-order-payment-method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {PAYMENT_METHOD_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                className="input mt-2"
                data-testid="manual-order-payment-note"
                placeholder="Note (e.g. Venmo tx #abc, comp reason)"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </div>
            <div>
              <p className="overline mb-2">Initial status</p>
              <select
                className="input"
                data-testid="manual-order-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="inline-flex items-center gap-2 mt-3 text-sm text-charcoal-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="manual-order-send-email"
                  checked={sendConfirmation}
                  onChange={(e) => setSendConfirmation(e.target.checked)}
                  disabled={!customer.email.trim()}
                />
                Email confirmation to customer
                {!customer.email.trim() && (
                  <span className="text-[11px] text-charcoal-400">
                    (needs an email)
                  </span>
                )}
              </label>
            </div>
          </section>

          {error && (
            <div
              data-testid="manual-order-error"
              className="p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-charcoal-100 bg-cream-200/40 rounded-b-3xl">
          <button
            type="button"
            data-testid="manual-order-cancel"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm border border-charcoal-200 hover:bg-cream-200"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="manual-order-submit"
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2 rounded-full text-sm font-medium bg-[#8B2252] text-white hover:bg-[#7a1e47] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Creating…" : "Create order"}
          </button>
        </div>
      </div>
    </div>
  );
}
